<script setup lang="ts">
import type { FilesCardProps, FilesCardEmits, FilesType } from './types'
import { CircleCloseFilled, View } from '@element-plus/icons-vue'
import { svgIconMap, colorMap } from './icons'

const props = withDefaults(defineProps<FilesCardProps>(), {
  uid: undefined,
  name: undefined,
  fileSize: undefined,
  fileType: undefined,
  description: undefined,
  url: undefined,
  thumbUrl: undefined,
  imgFile: undefined,
  iconSize: '42px',
  iconColor: undefined,
  showDelIcon: false,
  maxWidth: '236px',
  style: undefined,
  hoverStyle: undefined,
  imgVariant: 'rectangle',
  imgPreview: true,
  imgPreviewMask: true,
  status: undefined,
  percent: undefined,
  errorTip: undefined,
})

const emit = defineEmits<FilesCardEmits>()

function getFileType(ext: string): { lowerCase: FilesType; upperCase: string } {
  const map: Record<string, FilesType> = {
    doc: 'word', docx: 'word',
    xls: 'excel', xlsx: 'excel', csv: 'excel',
    ppt: 'ppt', pptx: 'ppt',
    pdf: 'pdf',
    txt: 'txt', log: 'txt',
    md: 'mark', markdown: 'mark',
    jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', svg: 'image', webp: 'image', bmp: 'image',
    mp3: 'audio', wav: 'audio', ogg: 'audio', flac: 'audio', aac: 'audio',
    mp4: 'video', avi: 'video', mov: 'video', wmv: 'video', flv: 'video', mkv: 'video',
    glb: 'three', gltf: 'three', obj: 'three', fbx: 'three',
    js: 'code', ts: 'code', py: 'code', java: 'code', cpp: 'code', c: 'code', go: 'code', rs: 'code',
    html: 'code', css: 'code', scss: 'code', vue: 'code', jsx: 'code', tsx: 'code',
    sql: 'database', db: 'database', sqlite: 'database',
    zip: 'zip', rar: 'zip', '7z': 'zip', tar: 'zip', gz: 'zip',
    lnk: 'link',
  }
  const lc = map[ext.toLowerCase()] || 'file'
  return { lowerCase: lc, upperCase: lc.charAt(0).toUpperCase() + lc.slice(1) }
}

function getSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(0)} GB`
}

// Parse filename into prefix + suffix (with .file fallback for long extensionless names)
const parsedName = computed(() => {
  const n = props.name || ''
  const dotIdx = n.lastIndexOf('.')
  if (dotIdx <= 0) {
    return n.length > 10 ? { prefix: n, suffix: '.file' } : { prefix: n, suffix: '' }
  }
  return { prefix: n.slice(0, dotIdx), suffix: n.slice(dotIdx) }
})

const _fileType = computed<FilesType>(() => {
  if (props.fileType) return props.fileType
  if (!props.name) return 'file'
  const dotIdx = props.name.lastIndexOf('.')
  if (dotIdx < 0) return 'file'
  return getFileType(props.name.slice(dotIdx + 1)).lowerCase
})

const _fileTypeUpperCase = computed(() => {
  if (props.fileType) return props.fileType.charAt(0).toUpperCase() + props.fileType.slice(1)
  if (!props.name) return 'File'
  const dotIdx = props.name.lastIndexOf('.')
  if (dotIdx < 0) return 'File'
  return getFileType(props.name.slice(dotIdx + 1)).upperCase
})

const _description = computed(() => {
  if (props.description) return props.description
  const typeStr = _fileTypeUpperCase.value
  const sizeStr = props.fileSize ? `・${getSize(props.fileSize)}` : ''
  if (props.status === 'uploading') return `上传中...・${props.percent || 0}%${sizeStr}`
  if (props.status === 'error') return props.errorTip || '上传失败'
  return `${typeStr}${sizeStr}`
})

const isImageFile = computed(() => _fileType.value === 'image')
const isSquareVariant = computed(() => props.imgVariant === 'square')

const _previewImg = ref<string | undefined>(undefined)
const _previewImgUrl = computed(() => {
  if (!isImageFile.value) return undefined
  if (props.thumbUrl) return props.thumbUrl
  if (props.url) return props.url
  return _previewImg.value
})

const _iconSize = computed(() => {
  if (isSquareVariant.value && isImageFile.value && (!props.iconSize || props.iconSize === '42px')) {
    return '64px'
  }
  return props.iconSize
})

watch(() => props.imgFile, async (f) => {
  if (f) {
    try {
      _previewImg.value = URL.createObjectURL(f as Blob)
    } catch { _previewImg.value = undefined }
  } else {
    if (_previewImg.value) URL.revokeObjectURL(_previewImg.value)
    _previewImg.value = undefined
  }
}, { deep: true, immediate: true })

const isHovered = ref(false)
const imageHovered = ref(false)
const imgRef = ref()

function handleDelete() {
  emit('delete', { ...props })
}

function handlePreviewAction(type: 'self' | 'mask') {
  if (props.imgPreview && imgRef.value && _previewImgUrl.value) {
    if (type === 'mask') imgRef.value.showPreview()
    else emit('imagePreview', { ...props })
  }
}

defineExpose({ namePrefix: computed(() => parsedName.value.prefix), nameSuffix: computed(() => parsedName.value.suffix), colorMap })
</script>

<template>
  <div
    :style="{
      '--elx-files-card-icon-size': _iconSize,
      '--elx-files-card-max-width': maxWidth,
      ...style,
      ...(isHovered && hoverStyle ? hoverStyle : {}),
    }"
    class="elx-files-card"
    :class="{
      'elx-files-card-square': isSquareVariant && isImageFile,
      'elx-files-card-hovered': isHovered,
      'elx-files-card-error': status === 'error',
      'elx-files-card-done': status === 'done',
      'elx-files-card-uploading': status === 'uploading',
    }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Upload progress bar -->
    <div
      v-if="status === 'uploading' && !(isSquareVariant && isImageFile)"
      class="elx-files-card-progress"
      :style="{ width: `${percent || 0}%` }"
    />

    <slot name="icon" v-bind="{ item: props }">
      <!-- Non-image file icon -->
      <component
        :is="svgIconMap[_fileType]"
        v-if="!isImageFile"
        class="elx-files-card-icon"
        :color="iconColor || colorMap[_fileType]"
      />

      <!-- Image file preview -->
      <template v-if="isImageFile">
        <div
          class="elx-files-card__image-preview"
          :class="{ 'elx-files-card__image-preview--square': isSquareVariant && isImageFile }"
          @mouseenter="imageHovered = true"
          @mouseleave="imageHovered = false"
        >
          <el-image
            v-if="_previewImgUrl"
            ref="imgRef"
            class="elx-files-card-img"
            :src="_previewImgUrl"
            :preview-src-list="imgPreview ? [_previewImgUrl] : []"
            fit="cover"
            :show-progress="false"
            hide-on-click-modal
            @show="handlePreviewAction('self')"
          />
          <component
            :is="svgIconMap[_fileType]"
            v-else
            class="elx-files-card-img"
            :color="iconColor || colorMap[_fileType]"
          />
          <!-- Preview mask on hover -->
          <Transition name="elx-files-card-fade">
            <slot
              v-if="imageHovered && _previewImgUrl && imgPreviewMask && imgPreview"
              name="image-preview-actions"
              v-bind="{ item: { ...props, prefix: parsedName.prefix, suffix: parsedName.suffix } }"
            >
              <div class="elx-files-card__preview-mask" @click="handlePreviewAction('mask')">
                <el-icon><View /></el-icon>
                <span>预览</span>
              </div>
            </slot>
          </Transition>
          <!-- Square uploading overlay -->
          <div v-if="status === 'uploading' && isSquareVariant" class="elx-files-card__preview-mask-loading">
            <el-progress color="#fff" type="circle" :percentage="percent" class="elx-files-card__circle-progress" />
          </div>
          <!-- Square error overlay -->
          <div v-else-if="status === 'error' && isSquareVariant" class="elx-files-card__preview-mask-error">
            <span class="error-text">{{ errorTip || '上传失败' }}</span>
          </div>
        </div>
      </template>
    </slot>

    <!-- File info (name + description) -->
    <slot
      v-if="($slots.content || name || description) && !(isSquareVariant && isImageFile)"
      name="content"
      v-bind="{ item: props }"
    >
      <div class="elx-files-card-content">
        <div v-if="name" class="elx-files-card-name">
          <slot name="name-prefix" v-bind="{ item: { ...props, prefix: parsedName.prefix, suffix: parsedName.suffix } }">
            <div class="elx-files-card-name-prefix">{{ parsedName.prefix }}</div>
          </slot>
          <slot name="name-suffix" v-bind="{ item: { ...props, prefix: parsedName.prefix, suffix: parsedName.suffix } }">
            <div class="elx-files-card-name-suffix">{{ parsedName.suffix }}</div>
          </slot>
        </div>
        <slot name="description" v-bind="{ item: { ...props, prefix: parsedName.prefix, suffix: parsedName.suffix } }">
          <div
            class="elx-files-card-description"
            :class="{
              'elx-files-card-description-error': status === 'error',
              'elx-files-card-description-done': status === 'done',
              'elx-files-card-description-uploading': status === 'uploading',
            }"
          >
            {{ _description }}
          </div>
        </slot>
      </div>
    </slot>

    <!-- Delete button -->
    <div v-if="showDelIcon && isHovered" class="elx-files-card-delete-icon" @click="handleDelete">
      <slot name="del-icon" v-bind="{ item: props }">
        <el-icon><CircleCloseFilled /></el-icon>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use './style.scss';
</style>
